/**
 * NotificationCenter Component
 *
 * Bell icon with unread badge and dropdown panel showing notifications.
 * Designed for the dashboard header.
 */

import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Bell,
  Check,
  CheckCheck,
  Trash2,
  X,
  Briefcase,
  MessageSquare,
  Clock,
  TrendingUp,
  Settings,
  User,
} from 'lucide-react'
import { formatRelativeTime } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import {
  useNotifications,
  useUnreadNotificationCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  useDeleteNotification,
  useClearAllNotifications,
} from '../hooks/useNotifications'
import type { ParsedNotification, NotificationType } from '@/shared/schemas/notification.schema'

/**
 * Get icon for notification type
 */
function getNotificationIcon(type: NotificationType) {
  switch (type) {
    case 'case_status_change':
      return Briefcase
    case 'task_due_soon':
    case 'task_overdue':
      return Clock
    case 'deal_stage_change':
      return TrendingUp
    case 'new_enquiry':
      return MessageSquare
    case 'mention':
      return User
    case 'assignment':
      return User
    case 'system':
    default:
      return Bell
  }
}

/**
 * Get color class for notification type
 */
function getNotificationColor(type: NotificationType): string {
  switch (type) {
    case 'task_overdue':
      return 'text-destructive'
    case 'task_due_soon':
      return 'text-warning'
    case 'deal_stage_change':
      return 'text-success'
    case 'new_enquiry':
      return 'text-primary'
    default:
      return 'text-muted-foreground'
  }
}

/**
 * Individual notification item
 */
function NotificationItem({
  notification,
  onMarkRead,
  onDelete,
  onNavigate,
}: {
  notification: ParsedNotification
  onMarkRead: (id: string, read: boolean) => void
  onDelete: (id: string) => void
  onNavigate: (link: string) => void
}) {
  const Icon = getNotificationIcon(notification.type)
  const colorClass = getNotificationColor(notification.type)

  const handleClick = () => {
    // Mark as read when clicked
    if (!notification.read) {
      onMarkRead(notification.id, true)
    }

    // Navigate if there's a link
    if (notification.data?.link) {
      onNavigate(notification.data.link)
    }
  }

  return (
    <div
      className={cn(
        'group flex items-start gap-3 p-3 hover:bg-accent/50 transition-colors cursor-pointer rounded-md',
        !notification.read && 'bg-accent/30'
      )}
      onClick={handleClick}
    >
      {/* Icon */}
      <div className={cn('mt-0.5 shrink-0', colorClass)}>
        <Icon className="h-4 w-4" />
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-sm leading-tight',
            !notification.read && 'font-medium'
          )}
        >
          {notification.title}
        </p>
        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">
          {notification.message}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          {formatRelativeTime(notification.createdAt)}
        </p>
      </div>

      {/* Actions (visible on hover) */}
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!notification.read ? (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation()
              onMarkRead(notification.id, true)
            }}
            title="Mark as read"
          >
            <Check className="h-3 w-3" />
          </Button>
        ) : (
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={(e) => {
              e.stopPropagation()
              onMarkRead(notification.id, false)
            }}
            title="Mark as unread"
          >
            <X className="h-3 w-3" />
          </Button>
        )}
        <Button
          variant="ghost"
          size="icon"
          className="h-6 w-6 text-destructive hover:text-destructive"
          onClick={(e) => {
            e.stopPropagation()
            onDelete(notification.id)
          }}
          title="Delete"
        >
          <Trash2 className="h-3 w-3" />
        </Button>
      </div>

      {/* Unread indicator */}
      {!notification.read && (
        <div className="shrink-0 mt-1.5">
          <div className="h-2 w-2 rounded-full bg-primary" />
        </div>
      )}
    </div>
  )
}

/**
 * Empty state when no notifications
 */
function EmptyState() {
  return (
    <div className="flex flex-col items-center justify-center py-8 px-4 text-center">
      <Bell className="h-10 w-10 text-muted-foreground/50 mb-3" />
      <p className="text-sm font-medium text-muted-foreground">
        No notifications
      </p>
      <p className="text-xs text-muted-foreground/70 mt-1">
        You're all caught up!
      </p>
    </div>
  )
}

/**
 * NotificationCenter - Bell icon with dropdown panel
 */
export function NotificationCenter() {
  const [open, setOpen] = useState(false)
  const navigate = useNavigate()

  const { data: notifications = [], isLoading } = useNotifications({ limit: 20 })
  const { data: unreadCount = 0 } = useUnreadNotificationCount()

  const markRead = useMarkNotificationRead()
  const markAllRead = useMarkAllNotificationsRead()
  const deleteNotification = useDeleteNotification()
  const clearAll = useClearAllNotifications()

  const handleMarkRead = (id: string, read: boolean) => {
    markRead.mutate({ id, read })
  }

  const handleDelete = (id: string) => {
    deleteNotification.mutate(id)
  }

  const handleNavigate = (link: string) => {
    setOpen(false)
    navigate(link)
  }

  const handleMarkAllRead = () => {
    markAllRead.mutate()
  }

  const handleClearAll = () => {
    if (window.confirm('Clear all notifications?')) {
      clearAll.mutate()
    }
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
              <span className="relative inline-flex h-4 w-4 items-center justify-center rounded-full bg-primary text-[10px] font-medium text-primary-foreground">
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-80 p-0"
        align="end"
        sideOffset={8}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <h3 className="font-semibold text-sm">Notifications</h3>
          <div className="flex items-center gap-1">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs"
                onClick={handleMarkAllRead}
                disabled={markAllRead.isPending}
              >
                <CheckCheck className="h-3 w-3 mr-1" />
                Mark all read
              </Button>
            )}
          </div>
        </div>

        {/* Notification list */}
        <ScrollArea className="max-h-[400px]">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkRead={handleMarkRead}
                  onDelete={handleDelete}
                  onNavigate={handleNavigate}
                />
              ))}
            </div>
          )}
        </ScrollArea>

        {/* Footer */}
        {notifications.length > 0 && (
          <>
            <Separator />
            <div className="flex items-center justify-between px-4 py-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-muted-foreground"
                onClick={() => {
                  setOpen(false)
                  navigate('/dashboard/settings')
                }}
              >
                <Settings className="h-3 w-3 mr-1" />
                Settings
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 text-xs text-destructive hover:text-destructive"
                onClick={handleClearAll}
                disabled={clearAll.isPending}
              >
                <Trash2 className="h-3 w-3 mr-1" />
                Clear all
              </Button>
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  )
}
